import { useEffect } from "react";
import { useList } from "@refinedev/core";
import { Link } from "react-router-dom";
import { useForm } from "@refinedev/react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Edit } from "lucide-react";
import { PageHeader } from "@/components/forms/PageHeader";
import { ToggleActivo } from "@/components/forms/ToggleActivo";
import { useAuth } from "@/hooks/useAuth";

interface Vendedor {
  id: string;
  nombre: string;
  documento: string | null;
  telefono: string | null;
  porc_comision: number;
  activo: boolean;
  tienda_id: string;
}

export function VendedoresPage() {
  const { data, isLoading } = useList<Vendedor>({
    resource: "vendedores",
    meta: { select: "*, tiendas(nombre)" },
  });

  return (
    <div>
      <PageHeader
        title="Vendedores"
        actions={
          <Link to="/admin/vendedores/nuevo" className="btn-primary inline-flex items-center gap-2">
            <Plus className="size-4" /> Nuevo
          </Link>
        }
      />
      {isLoading ? (
        <div className="card p-8 text-center text-fg-muted">Cargando...</div>
      ) : (
        <ul className="space-y-2">
          {data?.data.map((v) => (
            <li key={v.id} className="card p-4 flex items-center gap-3">
              <div className="flex-1">
                <p className="font-medium">{v.nombre}</p>
                <p className="text-sm text-fg-muted">
                  Comisión: {v.porc_comision}%
                  {v.telefono && ` · ${v.telefono}`}
                </p>
              </div>
              {!v.activo && <span className="text-xs text-error">Inactivo</span>}
              <ToggleActivo
                resource="vendedores"
                id={v.id}
                activo={v.activo}
                confirmarTexto={`¿Desactivar a "${v.nombre}"? Ya no aparecerá en el wizard de ventas.`}
              />
              <Link
                to={`/admin/vendedores/${v.id}/editar`}
                className="p-2 rounded-md hover:bg-bg"
                aria-label="Editar"
              >
                <Edit className="size-4" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function VendedorForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { user, isAdmin } = useAuth();
  const { data: tiendas } = useList({
    resource: "tiendas",
    filters: [{ field: "activo", operator: "eq", value: true }],
    pagination: { mode: "off" },
  });
  const {
    refineCore: { onFinish, formLoading },
    register,
    handleSubmit,
    setValue,
  } = useForm({
    refineCoreProps: {
      resource: "vendedores",
      action: isEdit ? "edit" : "create",
      id,
      redirect: false,
      onMutationSuccess: () => navigate("/admin/vendedores"),
    },
  });

  // Si no es admin (operador): preseleccionar su tienda y bloquear el campo.
  useEffect(() => {
    if (!isAdmin && user?.tienda_id && !isEdit) {
      setValue("tienda_id", user.tienda_id);
    }
  }, [isAdmin, user?.tienda_id, isEdit, setValue]);

  return (
    <div className="max-w-xl">
      <PageHeader title={isEdit ? "Editar vendedor" : "Nuevo vendedor"} />
      <form onSubmit={handleSubmit(onFinish)} className="card p-6 space-y-4">
        <Field label="Nombre">
          <input {...register("nombre", { required: true })} className="input" />
        </Field>
        <Field label="Documento">
          <input {...register("documento")} className="input" />
        </Field>
        <Field label="Teléfono">
          <input {...register("telefono")} className="input" />
        </Field>
        <Field label="% Comisión">
          <input
            type="number"
            step="0.1"
            {...register("porc_comision", { valueAsNumber: true })}
            className="input"
            defaultValue={0}
          />
        </Field>
        <Field label="Tienda">
          <select
            {...register("tienda_id", { required: true })}
            className="input"
            disabled={!isAdmin && !!user?.tienda_id}
          >
            <option value="">Selecciona...</option>
            {tiendas?.data.map((t) => (
              <option key={t.id} value={t.id}>{t.nombre as string}</option>
            ))}
          </select>
          {!isAdmin && (
            <p className="text-xs text-fg-muted">Asignado a tu tienda automáticamente.</p>
          )}
        </Field>
        <footer className="flex gap-2 pt-2">
          <button type="submit" disabled={formLoading} className="btn-primary">
            {formLoading ? "Guardando..." : "Guardar"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancelar
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}
